import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { VerticalConfig, VerificationMeta } from './atnaConfig';
export type Bi = {
  en: string;
  fr: string;
};
export type Label = {
  grade: 'A' | 'B' | 'C' | 'D';
  vi: number;
  source: number;
  concordance: number;
  freshness: number;
  type: 'FACT' | 'ESTIMATE';
  flags: Bi[];
  concordanceNote: Bi;
};
const F_ESTIMATE: Bi = {
  en: 'Illustrative',
  fr: 'Illustratif'
};
export const QUESTION_FR: Record<string, string> = {
  'How is ATNA different from a chatbot?': 'En quoi ATNA se distingue-t-elle d’un robot conversationnel ?',
  'How can ATNA help my organization do more with the same team?': 'Comment ATNA aide-t-elle mon organisation à faire plus avec la même équipe ?',
  'Which industries can use BCITI + ATNA?': 'Quels secteurs peuvent utiliser BCITI + ATNA ?',
  'Can BCITI integrate with our existing systems?': 'BCITI peut-elle s’intégrer à nos systèmes existants ?',
  'How long does deployment take?': 'Combien de temps prend le déploiement ?',
  'How does ATNA verify its answers?': 'Comment ATNA vérifie-t-elle ses réponses ?',
  'Can ATNA perform actions instead of only answering questions?': 'ATNA peut-elle exécuter des actions, pas seulement répondre ?',
  'Do you have customers in production?': 'Avez-vous des clients en production ?',
  'What business results can BCITI deliver?': 'Quels résultats d’affaires BCITI peut-elle générer ?',
  'Can I see a live demo?': 'Puis-je voir une démo en direct ?'
};
const SEARCH_ALIASES: Record<string, string[]> = {
  'How is ATNA different from a chatbot?': ['chatbot', 'bot', 'difference', 'different', 'assistant', 'robot', 'conversationnel', 'difference atna chatbot'],
  'How can ATNA help my organization do more with the same team?': ['capacity', 'team', 'same team', 'more with less', 'productivity', 'capacity team', 'equipe', 'capacité', 'faire plus'],
  'Which industries can use BCITI + ATNA?': ['industry', 'industries', 'sector', 'sectors', 'vertical', 'ville', 'health', 'airport', 'secteur', 'secteurs'],
  'Can BCITI integrate with our existing systems?': ['integrate', 'integration', 'api', 'crm', 'erp', 'sap', 'salesforce', 'legacy', 'system', 'systems', 'intégrer', 'integration', 'système'],
  'How long does deployment take?': ['deployment', 'deploy', 'timeline', 'how long', 'time', 'weeks', 'déploiement', 'combien', 'duree', 'durée'],
  'How does ATNA verify its answers?': ['verify', 'verification', 'verify answers', 'grade', 'score', 'source', 'concordance', 'verifie', 'vérifie', 'verification'],
  'Can ATNA perform actions instead of only answering questions?': ['action', 'actions', 'workflow', 'open request', 'form', 'schedule', 'execute', 'automate', 'exécuter', 'requête', 'formulaire'],
  'Do you have customers in production?': ['customer', 'customers', 'production', 'brossard', 'casselman', 'clarence', 'client', 'clients', 'preuve'],
  'What business results can BCITI deliver?': ['results', 'outcomes', 'roi', 'cost', 'costs', 'revenue', 'business', 'resultats', 'résultats', 'couts', 'coûts'],
  'Can I see a live demo?': ['demo', 'demonstration', 'live demo', 'meeting', 'book', 'démo', 'demonstration', 'rencontre']
};
export const questionLabel = (question: string, isFR: boolean) => isFR ? QUESTION_FR[question] ?? question : question;
export function normalizeSearchTerm(value: string) {
  return value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLocaleLowerCase().replace(/[^a-z0-9+\s]/g, ' ').replace(/\s+/g, ' ').trim();
}
export function findFaqQuestion(questions: string[], query: string) {
  const normalizedQuery = normalizeSearchTerm(query);
  if (!normalizedQuery) return null;
  const queryTokens = normalizedQuery.split(' ');
  let bestQuestion: string | null = null;
  let bestScore = 0;
  questions.forEach(question => {
    const searchable = [question, QUESTION_FR[question] ?? '', ...(SEARCH_ALIASES[question] ?? [])].map(normalizeSearchTerm).join(' ');
    const phraseMatch = searchable.includes(normalizedQuery);
    const tokenMatches = queryTokens.filter(token => token.length > 1 && searchable.includes(token)).length;
    const score = (phraseMatch ? 20 : 0) + tokenMatches;
    if (score > bestScore) {
      bestScore = score;
      bestQuestion = question;
    }
  });
  return bestScore > 0 ? bestQuestion : null;
}
function toLabel(meta: VerificationMeta): Label {
  return {
    grade: meta.grade,
    vi: meta.index,
    source: meta.source,
    concordance: meta.concordance,
    freshness: meta.freshness,
    type: 'ESTIMATE',
    flags: [F_ESTIMATE],
    concordanceNote: {
      en: 'Illustrative demonstration values',
      fr: 'Valeurs illustratives de démonstration'
    }
  };
}
export function resolveAnswer(vertical: VerticalConfig, question: string | null, isFR: boolean) {
  if (!question) return null;
  const answer = vertical.answers[question];
  if (!answer) return null;
  return {
    recommendation: isFR ? answer.recommendationFr : answer.recommendation,
    results: isFR ? answer.resultsFr : answer.results,
    caution: isFR ? answer.cautionFr : answer.caution,
    label: toLabel(answer.verification),
    modules: answer.modules
  };
}
export function VerificationScore({
  label,
  isFR,
  expanded,
  onToggle
}: {
  label: Label;
  isFR: boolean;
  expanded: boolean;
  onToggle: () => void;
}) {
  return <div className="border border-[#E4E6EC] flex items-center justify-between px-4 py-3 gap-3" data-id="element-708" data-mp="helpdjw03ohr">
      <div className="flex items-center gap-3 min-w-0" data-id="element-709" data-mp="1kpj45t10w8i9">
        <div className="w-11 h-11 shrink-0 border border-[#4F46E5]/30 bg-[#4F46E5]/[0.04] flex items-center justify-center" data-id="element-710" data-mp="1fqy4buponquc">
          <span className="font-serif text-lg text-[#4F46E5] leading-none" data-id="element-711" data-mp="1trinirzifood">
            {label.vi.toFixed(2)}
          </span>
        </div>
        <div className="min-w-0" data-id="element-712" data-mp="23o56tcc6u7oa">
          <div className="font-sans text-[11px] font-semibold tracking-[0.12em] uppercase text-[#5B6478]" data-id="element-713" data-mp="23nbza95ene53">
            {isFR ? 'Indice de vérification' : 'Verification index'}
          </div>
          <div className="font-sans text-sm text-[#121212]" data-id="element-714" data-mp="2cgow3m6lazwg">
            {isFR ? 'Cote' : 'Grade'} {label.grade} · {isFR ? 'Illustratif' : 'Illustrative'}
          </div>
        </div>
      </div>
      <button onClick={onToggle} className="shrink-0 inline-flex items-center gap-1 font-sans text-sm font-medium text-[#4F46E5] hover:text-[#4F46E5]/80 transition-colors" data-id="element-715" data-mp="2me3a7f51jcll">
        {expanded ? isFR ? 'Masquer' : 'Hide' : isFR ? 'Voir les détails' : 'View details'}
        {expanded ? <ChevronUpIcon className="w-4 h-4" data-id="element-716" data-mp="2r3e6wk6akzi5" /> : <ChevronDownIcon className="w-4 h-4" data-id="element-717" data-mp="2r2w2h09h7ez5" />}
      </button>
    </div>;
}
export function VerificationLabel({
  label,
  isFR,
  compact = false
}: {
  label: Label;
  isFR: boolean;
  compact?: boolean;
}) {
  const padding = compact ? 'px-4 py-2.5' : 'px-5 py-3';
  return <div className="border border-[#E4E6EC]" data-id="element-718" data-mp="2n33pmbd1umnz">
      <div className={`${padding} border-b border-[#E4E6EC] font-sans text-xs font-semibold tracking-[0.15em] text-[#121212] uppercase`} data-id="element-719" data-mp="28co04kdlosk0">
        {isFR ? 'Étiquette de vérification ATNA' : 'ATNA verification label'}
      </div>
      <div className="flex items-stretch border-b border-[#E4E6EC]" data-id="element-720" data-mp="1zd9epzcaj86d">
        <div className={`flex items-center justify-center ${compact ? 'w-16' : 'w-24'} border-r border-[#E4E6EC] bg-[#4F46E5]/[0.03]`} data-id="element-721" data-mp="23e328rltmkyq">
          <span className={`font-serif ${compact ? 'text-3xl' : 'text-4xl'} text-[#4F46E5]`} data-id="element-722" data-mp="2jqzt68xhx74d">
            {label.grade}
          </span>
        </div>
        <div className={`flex-1 ${compact ? 'px-4 py-3' : 'px-5 py-4'}`} data-id="element-723" data-mp="29tlb64vxca9y">
          <div className="font-sans text-sm font-medium text-[#121212]" data-id="element-724" data-mp="24wl69xat9dkf">
            {isFR ? 'Cote' : 'Grade'} {label.grade}
          </div>
          <div className="font-sans text-xs text-[#5B6478] mt-1" data-id="element-725" data-mp="20vj3x7llkle8">
            {isFR ? 'Indice de vérification' : 'Verification index'}{' '}
            <span className="font-mono text-[#121212]" data-id="element-726" data-mp="1r459he8i6bry">
              {label.vi.toFixed(2)}
            </span>
          </div>
        </div>
      </div>
      <div className="divide-y divide-[#E4E6EC] border-b border-[#E4E6EC]" data-id="element-727" data-mp="1m6vezk69b9x9">
        <AxisRow label="Source" value={label.source} compact={compact} data-id="element-728" data-mp="1h55yhp09m41s" />
        <AxisRow label="Concordance" value={label.concordance} compact={compact} data-id="element-729" data-mp="1d4vwsoji9zsq" />
        <AxisRow label={isFR ? 'Fraîcheur' : 'Freshness'} value={label.freshness} compact={compact} data-id="element-730" data-mp="3osr0ih7xv9wk" />
      </div>
      <div className={`flex items-center justify-between ${padding}`} data-id="element-731" data-mp="3svf8kmotwcsh">
        <span className="font-sans text-sm text-[#5B6478]" data-id="element-732" data-mp="1nswc5o1n200">
          {isFR ? 'Cadre' : 'Context'}
        </span>
        <span className="font-sans text-sm text-[#121212]" data-id="element-733" data-mp="6lkydsiek4hx">
          {isFR ? 'Démonstration illustrative' : 'Illustrative demonstration'}
        </span>
      </div>
    </div>;
}
function AxisRow({
  label,
  value,
  compact
}: {
  label: string;
  value: number;
  compact?: boolean;
}) {
  return <div className={compact ? 'px-4 py-2.5' : 'px-5 py-3'} data-id="element-734" data-mp="3f032jj582zw9">
      <div className="flex items-center justify-between mb-1.5" data-id="element-735" data-mp="3jtxu18r2kj5w">
        <span className="font-sans text-sm text-[#5B6478]" data-id="element-736" data-mp="2mds34btcr511">{label}</span>
        <span className="font-mono text-sm text-[#121212]" data-id="element-737" data-mp="2mbe8g42l3uu0">
          {value.toFixed(2)}
        </span>
      </div>
      <div className="h-1 bg-[#E4E6EC]" data-id="element-738" data-mp="3r3cegi80aw8k">
        <div className="h-1 bg-[#4F46E5]" style={{
        width: `${Math.round(value * 100)}%`
      }} data-id="element-739" data-mp="3r2arbfdpujah" />
      </div>
    </div>;
}
export function WhyThis({
  label,
  isFR
}: {
  label: Label;
  isFR: boolean;
}) {
  return <div className="border border-[#E4E6EC] bg-[#FAFAFB] p-5" data-id="element-740" data-mp="9v77wimh3zc2">
      <div className="font-sans text-sm font-semibold text-[#121212] mb-4" data-id="element-741" data-mp="9up5f497wovp">
        {isFR ? 'Pourquoi cette réponse ?' : 'Why this answer?'}
      </div>
      <div className="space-y-2 font-sans text-sm" data-id="element-742" data-mp="3dbebyq8svyld">
        <div className="flex justify-between gap-4" data-id="element-743" data-mp="3dc78kye1p7ps">
          <span className="text-[#5B6478]" data-id="element-744" data-mp="3iah9lpmgb2jb">Source</span>
          <span className="text-[#121212] text-right" data-id="element-745" data-mp="3i8xptp1l69cs">
            {isFR ? 'Base de connaissances vérifiée + systèmes connectés' : 'Verified knowledge base + connected systems'}
          </span>
        </div>
        <div className="flex justify-between gap-4" data-id="element-746" data-mp="2ukwksq3m9b24">
          <span className="text-[#5B6478]" data-id="element-747" data-mp="2ujlfgluivv8w">Concordance</span>
          <span className="text-[#121212] text-right" data-id="element-748" data-mp="1bpaaqapbdj7l">
            {isFR ? label.concordanceNote.fr : label.concordanceNote.en}
          </span>
        </div>
        <div className="flex justify-between gap-4" data-id="element-749" data-mp="17mvodgmfs1bk">
          <span className="text-[#5B6478]" data-id="element-750" data-mp="12oc2thbr8rjq">
            {isFR ? 'Fraîcheur' : 'Freshness'}
          </span>
          <span className="text-[#121212] text-right" data-id="element-751" data-mp="xq0u59x74n33">
            {isFR ? 'Mis à jour aujourd’hui' : 'Updated today'}
          </span>
        </div>
      </div>
    </div>;
}
export function InformActNote({
  isFR
}: {
  isFR: boolean;
}) {
  return <div className="flex items-start gap-3" data-id="element-752" data-mp="jyw32w7w934d">
      <CheckIcon className="w-4 h-4 text-[#4F46E5] shrink-0 mt-1" strokeWidth={2.25} data-id="element-753" data-mp="jzxbesxizbie" />
      <p className="font-sans text-sm text-[#5B6478] leading-relaxed" data-id="element-754" data-mp="f1m1ki78d106">
        {isFR ? 'Réponse informative — la personne décide en s’appuyant sur l’étiquette affichée. Toute action exige une autorisation ; ATNA n’exécute jamais automatiquement.' : 'Informational answer — a person makes the decision based on the displayed label. Any action requires authorization; ATNA never executes automatically.'}
      </p>
    </div>;
}
export function BuildingSequence({
  isFR
}: {
  isFR: boolean;
}) {
  return <motion.p initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className="font-sans text-sm text-[#5B6478]" data-id="element-755" data-mp="3ilbiqn2mtbvv">
      {isFR ? 'ATNA prépare l’étiquette de vérification…' : 'ATNA is preparing the verification label…'}
    </motion.p>;
}